import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Download, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import { uploadContractsCSV, downloadContractTemplate } from '../config/api';
import { toast } from 'react-hot-toast';

const ContractCSVUpload = ({ onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pending'
    })));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    multiple: true
  });

  const removeFile = (fileId) => {
    setFiles(files.filter(f => f.id !== fileId));
  };

  const downloadTemplate = async () => {
    try {
      const blob = await downloadContractTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contract-upload-template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    const results = [];

    for (const fileItem of files) {
      try {
        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 'uploading' }));
        
        const result = await uploadContractsCSV(fileItem.file);
        
        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 'success' }));
        results.push({ file: fileItem.file.name, success: true, result });
        
        toast.success(`${fileItem.file.name} uploaded successfully!`);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 'error' }));
        results.push({ 
          file: fileItem.file.name, 
          success: false, 
          error: error.response?.data?.error || error.message 
        });
        
        toast.error(`${fileItem.file.name} upload failed: ${error.response?.data?.error || error.message}`);
      }
    }

    setUploading(false);
    
    if (onUploadComplete) {
      onUploadComplete(results);
    }

    // Clear files after upload
    setTimeout(() => {
      setFiles([]);
      setUploadProgress({});
    }, 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'uploading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>;
      default:
        return <FileText className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return 'Uploaded successfully';
      case 'error':
        return 'Upload failed';
      case 'uploading':
        return 'Uploading...';
      default:
        return 'Pending';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Contracts via CSV</h3>
        <p className="text-sm text-gray-600">
          Upload multiple contracts using CSV files. Download the template below to see the required format.
        </p>
      </div>

      {/* Template Download */}
      <div className="flex justify-center">
        <button
          onClick={downloadTemplate}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          title="Download template"
        >
          <Download className="h-4 w-4 mr-2" />
          Download Template
        </button>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-lg font-medium text-gray-900 mb-2">
          {isDragActive ? 'Drop the CSV files here' : 'Drag & drop CSV files here'}
        </p>
        <p className="text-sm text-gray-500">
          or click to select files
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Supports .csv files only
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Selected Files:</h4>
          {files.map((fileItem) => (
            <div
              key={fileItem.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                {getStatusIcon(uploadProgress[fileItem.id] || 'pending')}
                <div>
                  <p className="text-sm font-medium text-gray-900">{fileItem.file.name}</p>
                  <p className="text-xs text-gray-500">
                    {getStatusText(uploadProgress[fileItem.id] || 'pending')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => removeFile(fileItem.id)}
                className="text-gray-400 hover:text-gray-600"
                disabled={uploading}
                title="Remove file"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {files.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Upload contracts"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Contracts
              </>
            )}
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">📋 Instructions:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>contract_number</strong>: Required, unique identifier</li>
          <li>• <strong>description</strong>: Required, contract description</li>
          <li>• <strong>type</strong>: DIRECT or INDIRECT (defaults to INDIRECT)</li>
          <li>• <strong>status</strong>: DRAFT, ACTIVE, EXPIRED, or ARCHIVED (defaults to DRAFT)</li>
          <li>• <strong>effective_from</strong>: Required, start date (YYYY-MM-DD)</li>
          <li>• <strong>effective_to</strong>: End date (YYYY-MM-DD)</li>
          <li>• <strong>customer_edi_id</strong>: Customer EDI ID to link (optional)</li>
          <li>• <strong>price_notify_mode</strong>: EDI, MANUAL, or EMAIL (defaults to MANUAL)</li>
        </ul>
        <p className="text-xs text-blue-600 mt-2">
          💡 <strong>Note:</strong> If a contract with the same number already exists, it will be updated instead of created.
        </p>
      </div>
    </div>
  );
};

export default ContractCSVUpload;
